import { create } from "zustand";
import keycloak, {
  initKeycloak,
  login as kcLogin,
  logout as kcLogout,
  register as kcRegister,
  getUserProfile,
  getToken,
  isAuthenticated as kcIsAuthenticated,
  hasRole,
} from "@/lib/keycloak";
import db, { storage, functions } from "auth/firebase";

// ─── Types ─────────────────────────────────────────────────────────────────

export interface UserProfile {
  id?: string;
  email?: string;
  emailVerified?: boolean;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  preferredUsername?: string;
  phone?: string;
  tenantAssociations?: string[];
  verificationLevel?: number;
  roles: string[];
  resourceAccess?: Record<string, { roles: string[] }>;
}

/** Application entry derived from Keycloak resource_access token claim */
export interface AccessibleApp {
  clientId: string;
  name: string;
  icon: string;
  url: string;
  roles: string[];
}

/**
 * Maps Keycloak client IDs to display metadata.
 * Each key must match a client ID registered in Keycloak.
 */
export const APP_CATALOG: Record<string, { name: string; icon: string; url: string }> = {
  // ─── Core apps with icon images ────────────────────────────────────────────
  karemanager: {
    name: "KareManager",
    icon: "/src/assets/appicons/kareManager.png",
    url: "https://cloud.collabkare.app",
  },
  "kare-portal": {
    name: "KarePortal",
    icon: "/src/assets/appicons/karePortal.png",
    url: "https://kareportal.collabkare.com",
  },
  karemd: {
    name: "KareMD",
    icon: "/src/assets/appicons/kareMD.png",
    url: "https://karemd.collabkare.com",
  },
  karepractice: {
    name: "KarePractice",
    icon: "/src/assets/appicons/karePractice.png",
    url: "https://karepractice.collabkare.com",
  },
  kareintelligence: {
    name: "KareIntelligence",
    icon: "/src/assets/appicons/kareIntelligence.png",
    url: "https://karemanager.collabkare.com",
  },
  // ─── Additional apps ──────────────────────────────────────────────────────
  mykare: {
    name: "MyKare Portal",
    icon: "",
    url: "https://my.collabkare.health",
  },
  cloudmanager: {
    name: "Cloud Manager",
    icon: "",
    url: "https://cloud.collabkare.app",
  },
  "collabkare-ai": {
    name: "CollabKare AI",
    icon: "",
    url: "https://ai.collabkare.app",
  },
  "collabkare-health": {
    name: "CollabKare Health",
    icon: "",
    url: "https://health.collabkare.app",
  },
  pophealth: {
    name: "Population Health",
    icon: "",
    url: "https://pophealth.collabkare.app",
  },
  him: {
    name: "HIM",
    icon: "",
    url: "https://him.collabkare.app",
  },
  karepartner: {
    name: "KarePartner",
    icon: "",
    url: "https://karepartner.collabkare.com",
  },
  bi: {
    name: "Business Intelligence",
    icon: "",
    url: "https://bi.collabkare.app",
  },
  "admin-panel": {
    name: "Admin Panel",
    icon: "",
    url: "https://admin.collabkare.app",
  },
  "my-collabkare-id": {
    name: "My CollabKare ID",
    icon: "",
    url: "https://my.collabkare.id",
  },
};

export interface FirebaseUserData {
  name?: string;
  email?: string;
  phone?: string;
  delegatedApplications?: string[];
  [key: string]: unknown;
}

interface AuthState {
  // State
  isAuthenticated: boolean;
  isInitialized: boolean;
  userProfile: UserProfile | null;
  firebaseUserData: FirebaseUserData | null;
  userImage: string | null;
  userRoles: string[];
  accessibleApps: AccessibleApp[];

  // Keycloak lifecycle
  initialize: () => Promise<boolean>;
  login: (redirectUri?: string) => void;
  register: (redirectUri?: string) => void;
  logout: (redirectUri?: string) => void;

  // Token helpers
  getAccessToken: () => Promise<string | undefined>;
  hasRole: (role: string) => boolean;

  // Firebase user data (mirror)
  fetchFirebaseUserData: () => Promise<void>;
  setUserImage: (imageUrl: string) => void;

  // Legacy compat: expose isAuthenticated check + roles as numbers for old components
  getUserRolesAsNumbers: () => number[];

  // True if the user is a clinician/provider (has access to a clinical app) —
  // gates professional-only features like ABDM HPR from patients.
  isClinician: () => boolean;
}

/**
 * Keycloak client IDs that denote a clinician / healthcare-staff context.
 * A patient holds only patient apps (kare-portal, mykare, collabkare-health,
 * my-collabkare-id), so the presence of any of these marks a professional.
 */
const CLINICAL_CLIENTS = new Set<string>([
  "karemd",
  "karepractice",
  "karemanager",
  "kareintelligence",
  "cloudmanager",
  "pophealth",
  "him",
  "karepartner",
  "bi",
  "admin-panel",
]);

/**
 * Realm roles that DON'T imply a clinician — Keycloak defaults + the patient
 * role. Anyone holding a realm role OUTSIDE this set is treated as a
 * professional (solo practitioners may carry only a realm role like
 * "practitioner"/"provider" and no per-app client role, so an app-only check
 * would wrongly exclude them). Tune this denylist as the taxonomy firms up.
 */
const NON_CLINICAL_REALM_ROLES = new Set<string>([
  "offline_access",
  "uma_authorization",
  "default-roles-collabkare",
  "patient",
  "user",
  "member",
]);

/**
 * Derive accessible apps from the Keycloak resource_access token claim.
 * Only includes apps that exist in APP_CATALOG (skip account, realm-management, etc.).
 */
function deriveAccessibleApps(
  resourceAccess: Record<string, { roles: string[] }>
): AccessibleApp[] {
  return Object.entries(resourceAccess)
    .filter(([clientId]) => clientId in APP_CATALOG)
    .map(([clientId, access]) => ({
      clientId,
      ...APP_CATALOG[clientId],
      roles: access.roles || [],
    }));
}

export const useAuthStore = create<AuthState>((set, get) => ({
  // ─── Initial State ───────────────────────────────────────────────────────
  isAuthenticated: false,
  isInitialized: false,
  userProfile: null,
  firebaseUserData: null,
  userImage: null,
  userRoles: [],
  accessibleApps: [],

  // ─── Keycloak Lifecycle ──────────────────────────────────────────────────

  initialize: async () => {
    try {
      const authenticated = await initKeycloak();

      if (authenticated) {
        const profile = getUserProfile();
        const apps = deriveAccessibleApps(profile?.resourceAccess || {});
        set({
          isAuthenticated: true,
          isInitialized: true,
          userProfile: profile,
          userRoles: profile?.roles || [],
          accessibleApps: apps,
        });

        // Fetch Firebase user data in background
        get().fetchFirebaseUserData();
      } else {
        set({
          isAuthenticated: false,
          isInitialized: true,
          userProfile: null,
          userRoles: [],
        });
      }

      // Listen for Keycloak events to keep store in sync
      keycloak.onAuthSuccess = () => {
        const profile = getUserProfile();
        const apps = deriveAccessibleApps(profile?.resourceAccess || {});
        set({
          isAuthenticated: true,
          userProfile: profile,
          userRoles: profile?.roles || [],
          accessibleApps: apps,
        });
        get().fetchFirebaseUserData();
      };

      keycloak.onAuthLogout = () => {
        set({
          isAuthenticated: false,
          userProfile: null,
          firebaseUserData: null,
          userImage: null,
          userRoles: [],
          accessibleApps: [],
        });
      };

      return authenticated;
    } catch (error) {
      console.error("Auth initialization failed:", error);
      set({ isAuthenticated: false, isInitialized: true });
      return false;
    }
  },

  login: (redirectUri) => {
    kcLogin(redirectUri);
  },

  register: (redirectUri) => {
    kcRegister(redirectUri);
  },

  logout: (redirectUri) => {
    set({
      isAuthenticated: false,
      userProfile: null,
      firebaseUserData: null,
      userImage: null,
      userRoles: [],
    });
    kcLogout(redirectUri);
  },

  // ─── Token Helpers ───────────────────────────────────────────────────────

  getAccessToken: async () => {
    return getToken();
  },

  hasRole: (role) => {
    return hasRole(role);
  },

  // ─── Firebase User Data ──────────────────────────────────────────────────

  fetchFirebaseUserData: async () => {
    const profile = get().userProfile;
    if (!profile?.email) return;

    try {
      // Fetch user data from Firebase Cloud Function (same as existing getUsersInfo)
      const getUsersInfo = functions.httpsCallable("getUsersInfo");
      const result = await getUsersInfo({ email: profile.email });
      const userData = result.data as FirebaseUserData;

      // Fetch profile picture from Firebase Storage
      let imageUrl: string | null = null;
      try {
        const imageResult = await storage
          .ref()
          .child(`documents/users_dp/${profile.email}/`)
          .listAll();
        if (imageResult.items.length > 0) {
          imageUrl = await imageResult.items[0].getDownloadURL();
        }
      } catch (imgError) {
        console.warn("Could not fetch profile image:", imgError);
      }

      set({
        firebaseUserData: userData,
        userImage: imageUrl,
      });
    } catch (error) {
      console.error("Failed to fetch Firebase user data:", error);
    }
  },

  setUserImage: (imageUrl) => {
    set({ userImage: imageUrl });
  },

  // ─── Legacy Compatibility ────────────────────────────────────────────────

  /**
   * Convert string roles to numeric roles for legacy components
   * that use userRole.includes(13) etc.
   * Maps Keycloak realm roles to the numeric codes used in old Redux state.
   */
  getUserRolesAsNumbers: () => {
    const { firebaseUserData } = get();
    // If Firebase user data has roles from the old system, use those
    if (firebaseUserData && Array.isArray((firebaseUserData as Record<string, unknown>).roles)) {
      return (firebaseUserData as Record<string, unknown>).roles as number[];
    }
    return [];
  },

  isClinician: () => {
    const { accessibleApps, userProfile } = get();
    // 1) Has a clinical app (staff/provider with a clinical client role).
    if ((accessibleApps || []).some((a) => CLINICAL_CLIENTS.has(a.clientId))) return true;
    // 2) Holds any realm role beyond the default/patient set — covers solo
    //    practitioners marked by a realm role rather than a per-app client role.
    const realmRoles = (userProfile?.roles as string[] | undefined) || [];
    return realmRoles.some((r) => !NON_CLINICAL_REALM_ROLES.has(r));
  },
}));
